import React, { useState, useEffect } from 'react';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory } from '../utils/i18n';
import { 
  Tractor, FlaskConical, Map, Sprout, Coins, Layers,
  Download, Copy, Check, ArrowLeft, Sparkles, 
  FileText, Upload, RefreshCw, FileCheck, X, Globe, ShieldCheck, AlertTriangle
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import ExcelExportButton from './ExcelExportButton';
import { DigitalSignatureModal } from './DigitalSignatureModal';
import { getFieldLabel, getFieldValue, getNotesText } from '../utils/demoPresetsI18n';

interface AgriAgentViewProps {
  agentId: string;
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const DEFAULT_AGRI_DEMO: Record<string, {
  field1LabelUk: string; field1Value: string;
  field2LabelUk: string; field2Value: string;
  field3LabelUk: string; field3Value: string;
  field4LabelUk: string; field4Value: string;
  field5LabelUk: string; field5Value: string;
  field6LabelUk: string; field6Value: string;
  notesText: string;
}> = {
  'agri-grain-quality-elevator-auditor': {
    field1LabelUk: "Культура & Фізична вага партії (брутто)",
    field1Value: "Пшениця продовольча 2 клас / Фізична маса за ТТН: 1 450.00 тонн (Партія № 08/2026)",
    field2LabelUk: "Стандарт якості & Базисні кондиції контракту",
    field2Value: "ДСТУ 3768:2019 / GAFTA 78 (Базис: Вологість 14.0%, Смітна домішка 2.0%, Білок 12.5%, Число падіння ≥ 220 с)",
    field3LabelUk: "Фактичні лабораторні показники якості",
    field3Value: "Фактична вологість: 16.2% (+2.2%), Смітна домішка: 3.8% (+1.8%), Білок: 13.1% (+0.6%), Число падіння: 265 с",
    field4LabelUk: "Розрахунок убутку за формулою Дюваля (т)",
    field4Value: "Убуток вологості: 37.12 т, Убуток сміття: 26.68 т, Залікова маса після доробки: 1 386.20 тонн",
    field5LabelUk: "Аудит тарифів елеватора (Сушіння / Очищення)",
    field5Value: "Тариф сушіння: 185 грн/т-%, Очищення: 95 грн/т / Загальна вартість послуг елеватора: 728 400 грн",
    field6LabelUk: "Розрахунок боніфікацій & рефакцій за контрактом",
    field6Value: "Боніфікація за підвищений білок (+0.6%): +$3.50/т / Рефакція за затримку вологості: -$4.20/т",
    notesText: "Провести повний аудит складських квитанцій форми № 13 та реєстрів ТТН елеватора. Перевірити правильність застосування формули Дюваля для розрахунку залікової кондиційної маси зернової партії, виявити можливі приховані списання ваги, розрахувати штрафні рефакції та преміальні боніфікації за експортним контрактом GAFTA 78 і скласти підсумковий Розрахунковий Акт у Word (.docx) за ДСТУ 4163:2020 та Excel (.xlsx з формулами)."
  },
  'agri-cadastre-gis-auditor': {
    field1LabelUk: "Кількість земельних ділянок / Площа зембанку",
    field1Value: "142 земельні ділянки (паї) / Загальна площа: 620 га ріллі (SHP контури полів)",
    field2LabelUk: "Кадастрові накладки & Перетини меж (ДЗК / EGiB)",
    field2Value: "Виявлено 5 просторових накладок меж (перетин від 0.15 до 0.8 га з суміжними землекористувачами)",
    field3LabelUk: "Терміни закінчення оренди & Ризик 'шахматки'",
    field3Value: "У 2026-2027 рр. закінчуються договори на 38 ділянок (165 га) — ризик захоплення конкурентами",
    field4LabelUk: "Нормативна грошова оцінка (НГО 2026) & Орендна плата",
    field4Value: "Базова НГО ріллі: 31 500 грн/га (з коефіцієнтом індексації 1.051) / Орендна плата: 12% від НГО",
    field5LabelUk: "Статус реєстрації прав у ДРРП / Реєстрі прав",
    field5Value: "Зареєстровано право оренди на 134 ділянки / 8 ділянок перебувають на стадії перереєстрації",
    field6LabelUk: "Формат геопросторових даних",
    field6Value: "WGS 84 / EPSG:3857, ESRI Shapefile (.shp), GeoJSON з контурами масивів",
    notesText: "Здійснити комплексний просторовий та юридичний аудит земельного банку агропідприємства площею 620 га. Виявити та локалізувати накладки кадастрових меж ділянок за даними ДЗК/EGiB. Сформувати реєстр критичних договорів оренди, термін дії яких спливає у 2026-2027 роках, розрахувати втрати та підготувати проєкти додаткових угод на пролонгацію оренди у Word (.docx) та Excel (.xlsx) за ДСТУ 4163:2020."
  },
  'agri-eu-cap-subsidies-advisor': {
    field1LabelUk: "Категорія господарства & Загальна площа (га)",
    field1Value: "Фермерське господарство / 350 га ріллі + 80 голів молочного ВРХ",
    field2LabelUk: "Цільові програми субсидій (ЄС CAP / ARiMR / ДАР)",
    field2Value: "Прямі виплати ЄС (JPO/BISS) + Екосхеми (Rolnictwo węglowe, Dobrostan zwierząt) + ДАР",
    field3LabelUk: "Вимоги умовності (GAEC & SMR комплаєнс)",
    field3Value: "GAEC 6 (мінімальний покрив ґрунту), GAEC 7 (сівозміна), GAEC 8 (невиробничі елементи 4%)",
    field4LabelUk: "Заплановані агрозаходи під екосхеми",
    field4Value: "Внесення гною/сепарату (4 бали), Озимий проміжний покрив (5 балів), Покращений добробут корів",
    field5LabelUk: "Оціночна сума фінансової підтримки (€ / PLN / грн)",
    field5Value: "Базові виплати: ~118 €/га + Екосхеми: ~95 €/га + Доплата на тваринництво = ~84 500 EUR / рік",
    field6LabelUk: "Платформа подання заявки",
    field6Value: "Портал eWniosekPlus (Польща/ЄС) та Кабінет Державного аграрного реєстру (ДАР Україна)",
    notesText: "Провести повний підбір та калькуляцію пакету прямих дотацій Спільної аграрної політики ЄС (CAP / WPR 2023-2027) та польського агентства ARiMR. Розрахувати максимальну бальну вигоду від участі в екосхемах (вуглецеве землеробство, біологічний захист, добробут тварин). Сформувати чек-лист відповідності нормам GAEC/SMR та підготувати Досьє субсидійної заявки у Word (.docx) та Excel за стандартами ЄС."
  },
  'agri-dar-consultant': {
    field1LabelUk: 'Форма власності & Площа земельного банку', field1Value: 'ФОП Агропідприємство / 450 га ріллі',
    field2LabelUk: 'Напрямок діяльності & Основні культури', field2Value: 'Рослинництво (Пшениця озима, Соняшник, Соя)',
    field3LabelUk: 'Статус реєстрації у ДАР (Державний аграрний реєстр)', field3Value: 'Зареєстровано (Кабінет ДАР підтверджено)',
    field4LabelUk: 'Регіон розташування & Кадастровий район', field4Value: 'Полтавська область / Миргородський район',
    field5LabelUk: 'Цільовий вид підтримки / гранту', field5Value: 'Компенсація вартості с/г техніки 25% + Грант на зрошення',
    field6LabelUk: 'Наявність заборгованості з податків (ЄСВ/ЄП)', field6Value: 'Заборгованість відсутня (Чистий статус ДПС)',
    notesText: 'Необхідно провести повний аналіз доступних державних субсидій та дотацій у ДАР для 450 га, розрахувати суму компенсації 25% на придбання сівалки точного висіву вартістю 2.4 млн грн, підготувати перелік необхідних документів для подачі до Мінагрополітики та згенерувати бізнес-план для агро-гранту.'
  },
  'agri-soil-analyst': {
    field1LabelUk: 'Площа поля & Культура-попередник', field1Value: '120 га / Попередник: Озимий ріпак',
    field2LabelUk: 'Запланована культура & Планова врожайність', field2Value: 'Кукурудза на зерно / Планова врожайність: 9.5 т/га',
    field3LabelUk: 'Тип ґрунту & Глибина відбору проб', field3Value: 'Чорнозем типовий / Глибина 0-30 см',
    field4LabelUk: 'Вміст поживних елементів (N, P, K мг/кг)', field4Value: 'N: 18 мг/кг (низький), P2O5: 95 мг/кг (середній), K2O: 140 мг/кг (високий)',
    field5LabelUk: 'Рівень pH (кислотність) & Гумус (%)', field5Value: 'pH: 5.4 (слабокислий) / Гумус: 3.2%',
    field6LabelUk: 'Вид добрив для внесення', field6Value: 'КАС-32, Амофос 12:52, Хлористий калій',
    notesText: 'Провести теплотехнічний та балансовий розрахунок винесення поживних речовин під 9.5 т/га кукурудзи. Сформувати систему диференційованого внесення добрив (N, P, K, Ca, Mg, S), розрахувати норму вапнування для нейтралізації кислотності (pH 5.4) та підготувати карту-схему живлення.'
  },
  'agri-land-audit': {
    field1LabelUk: 'Кількість земельних ділянок (паїв)', field1Value: '85 паїв / Загальна площа 340 га',
    field2LabelUk: 'Середня орендна плата (% від НГО)', field2Value: '12% від НГО (в середньому 4 200 грн/га/рік)',
    field3LabelUk: 'Терміни закінчення договорів оренди', field3Value: '2026-2027 рр. (потребують пролонгації 32 паї)',
    field4LabelUk: 'Регіон & Район розташування', field4Value: 'Вінницька область / Вінницький район',
    field5LabelUk: 'Наявність спірних паїв або подвійної реєстрації', field5Value: 'Виявлено 3 паї з ризиком подвійного обробітку',
    field6LabelUk: 'Форма розрахунку з пайовиками', field6Value: 'Грошова форма (80%) + Сільгосппродукція (20%)',
    notesText: 'Провести правовий аудит земельного банку підприємства (340 га), перевірити відповідність кадастрових номерів даним ДЗК та ДРРП, виявити ділянки із ризиком закінчення оренди у 2026 році, розрахувати суму ПДФО та військового збору з орендної плати та підготувати додаткові угоди на пролонгацію.'
  },
  'agri-yield-forecaster': {
    field1LabelUk: 'Культура & Площа посіву', field1Value: 'Соняшник високоолеїновий / 210 га',
    field2LabelUk: 'Поточний супутниковий індекс NDVI', field2Value: 'NDVI = 0.72 (висока вегетативна маса)',
    field3LabelUk: 'Індекс вегетаційного стресу & NDRE', field3Value: 'NDRE = 0.48, помірний азотний дефіцит на пд-заході поля',
    field4LabelUk: 'Фаза розвитку рослин', field4Value: 'Цвітіння - формування кошика (BBCH 61-65)',
    field5LabelUk: 'Сума ефективних температур & Опади', field5Value: 'СЕТ = 1450°C / Опади за травень-липень: 110 мм',
    field6LabelUk: 'Очікуваний термін збирання врожаю', field6Value: '10-15 вересня 2026 року',
    notesText: 'Аналіз супутникових знімків Sentinel-2 за липень 2026 показав неоднорідність вегетації на площі 210 га. Необхідно провести інтегральну оцінку стану посівів, розрахувати прогноз врожайності соняшника (план vs фактичний біопотенціал), надати рекомендації щодо позакореневого підживлення бором та десикації.'
  }
};

const LOADING_STEPS_UK = [
  "Ініціалізація мультиагентної команди Агро-Аналітиків (AgriTech & CrewAI)...",
  "Обробка просторових даних ГІС/SHP, реєстрів ДЗК, EGiB та кабінетів ДАР/ARiMR...",
  "Проведення кадастрових, агрономічних та економічних розрахунків (НГО, GAEC, екосхеми)...",
  "Аналіз юридичних ризиків закінчення оренди, перетинів меж та шахматки...",
  "Формування підсумкового Агро-Звіту та Паспорта Ділянки за ДСТУ 4163:2020..."
];

const LOADING_STEPS_PL = [
  "Inicjalizacja zespołu ekspertów rolnych, GIS i doradców dopłat ARiMR...",
  "Przetwarzanie granic działek z plików SHP, danych EGiB i bazy eWniosekPlus...",
  "Kalkulacja płatności bezpośrednich, ekoschematów i norm warunkowości GAEC/SMR...",
  "Weryfikacja nakładek granic działek i audyt terminów umów dzierżawy...",
  "Generowanie oficjalnego Raportu Agrotechnicznego i Wniosku Word (.docx)..."
];

const LOADING_STEPS_EN = [
  "Initializing precision agriculture, GIS cadastre & EU CAP subsidy multi-agent crew...",
  "Processing vector parcel boundaries (SHP/KML), cadastre records, and ARiMR/DAR portals...",
  "Calculating direct farm payments, GAEC cross-compliance & eco-schemes compensation...",
  "Auditing parcel overlaps, lease renewal timelines, and land tenure risks...",
  "Generating final Agri-Business & Land Dossier in Word (.docx)..."
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Tractor className={className} />;
  return <IconComponent className={className} />;
};

export default function AgriAgentView({
  agentId,
  language,
  onBack,
  onGoHome,
  useEnterpriseContext,
  enterpriseName
}: AgriAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const agentConfig = agentsConfig.find((a: any) => a.id === agentId) || {
    nameUk: 'Агро-Аналітичний Модуль',
    nameEn: 'Agriculture AI Agent',
    namePl: 'Moduł Rolniczy AI',
    descriptionUk: 'Аудит земельного банку, ДЗК, субсидії ЄС CAP та агрохімія.',
    descriptionEn: 'Land bank, cadastre, EU CAP subsidies and precision agriculture.',
    descriptionPl: 'Audyt banku ziemi, ewidencji gruntów EGiB, dopłat ARiMR i agrotechniki.',
    icon: 'Tractor',
    categoryUk: 'АГРОБІЗНЕС & ЗЕМЛЯ',
    categoryEn: 'AGRIBUSINESS & LAND',
    categoryPl: 'ROLNICTWO & GRUNTY'
  };

  const defaultDemo = DEFAULT_AGRI_DEMO[agentId] || DEFAULT_AGRI_DEMO['agri-cadastre-gis-auditor'] || DEFAULT_AGRI_DEMO['agri-dar-consultant'];

  const [field1, setField1] = useState(getFieldValue(defaultDemo, 1, language));
  const [field2, setField2] = useState(getFieldValue(defaultDemo, 2, language));
  const [field3, setField3] = useState(getFieldValue(defaultDemo, 3, language));
  const [field4, setField4] = useState(getFieldValue(defaultDemo, 4, language));
  const [field5, setField5] = useState(getFieldValue(defaultDemo, 5, language));
  const [field6, setField6] = useState(getFieldValue(defaultDemo, 6, language));
  const [notesText, setNotesText] = useState(getNotesText(defaultDemo, language));

  const [attachedFiles, setAttachedFiles] = useState<Array<{ name: string; size: number; text: string }>>([]);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [reportResult, setReportResult] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);

  useEffect(() => {
    setField1(getFieldValue(defaultDemo, 1, language));
    setField2(getFieldValue(defaultDemo, 2, language));
    setField3(getFieldValue(defaultDemo, 3, language));
    setField4(getFieldValue(defaultDemo, 4, language));
    setField5(getFieldValue(defaultDemo, 5, language));
    setField6(getFieldValue(defaultDemo, 6, language));
    setNotesText(getNotesText(defaultDemo, language));
    setReportResult(null);
  }, [defaultDemo, language]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isProcessing) {
      setLoadingStepIndex(0);
      const steps = isPl ? LOADING_STEPS_PL : isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex(prev => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing, isUk, isPl]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsReadingFile(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/extract-text', { method: 'POST', body: formData });

        if (res.ok) {
          const data = await res.json();
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: data.text || '' }]);
        } else {
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: `[Файл ${file.name} додано]` }]);
        }
      }
    } catch (err) {
      console.error('File upload error:', err);
    } finally {
      setIsReadingFile(false);
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRunAnalysis = async () => {
    setIsProcessing(true);
    setReportResult(null);
    setErrorMessage(null);
    setLoadingStepIndex(0);

    try {
      const combinedDocText = attachedFiles.map(f => `--- ФАЙЛ: ${f.name} ---\n${f.text}`).join('\n\n');
      
      const inputPrompt = `
[ЗАВДАННЯ ТА ВИХІДНІ ДАНІ АГРО-АУДИТУ / КАДАСТРУ / СУБСИДІЙ]:
${notesText}

[ВХІДНІ ПАРАМЕТРИ ЗЕМЕЛЬНОГО БАНКУ / АГРОНОМІЇ / ДОТАЦІЙ]:
- ${defaultDemo.field1LabelUk}: ${field1}
- ${defaultDemo.field2LabelUk}: ${field2}
- ${defaultDemo.field3LabelUk}: ${field3}
- ${defaultDemo.field4LabelUk}: ${field4}
- ${defaultDemo.field5LabelUk}: ${field5}
- ${defaultDemo.field6LabelUk}: ${field6}

${combinedDocText ? `[ДОДАНІ РЕЄСТРИ / ГІС ДАНІ / ВИПИСКИ ДЗК / ДАР]:\n${combinedDocText}` : ''}
`;

      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: agentConfig.nameUk || "Аудитор Земельного Банку, ДЗК & Накладок Ділянок (SHP / KML / GIS)",
          agentId: agentId,
          inputs: {
            task_description: inputPrompt,
            agent_name: agentConfig.nameUk,
            target_language: isPl ? 'pl' : isUk ? 'uk' : 'en'
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isPl ? 'Błąd wykonania analizy rolnej/GIS.' : isUk ? 'Помилка виконання агро-аналізу/ГІС.' : 'Execution failed.'));
      }

      const data = await response.json();

      if (data.jobId) {
        const pollJob = async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (!statusRes.ok) {
              throw new Error(isPl ? 'Błąd sprawdzania statusu zadania.' : isUk ? 'Помилка перевірки статусу завдання.' : 'Status check failed.');
            }
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              const rawReport = statusData.report || statusData.result || '';
              const resText = typeof rawReport === 'string'
                ? rawReport
                : (typeof rawReport === 'object' && rawReport !== null
                    ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2))
                    : String(rawReport));

              if (!resText) {
                throw new Error(isPl ? 'Serwer zwrócił pusty raport.' : isUk ? 'Сервер повернув порожній результат звіту.' : 'Server returned empty report.');
              }
              setReportResult(resText);
              setIsProcessing(false);
            } else if (statusData.status === 'failed') {
              setErrorMessage(statusData.error || (isPl ? 'Błąd generowania raportu.' : isUk ? 'Помилка генерації звіту.' : 'Report generation failed.'));
              setIsProcessing(false);
            } else {
              setTimeout(pollJob, 2500);
            }
          } catch (pollErr: any) {
            console.error('Polling error:', pollErr);
            setErrorMessage(pollErr.message || (isPl ? 'Wystąpił błąd podczas pobierania wyników.' : isUk ? 'Сталася помилка при отриманні результатів.' : 'Polling error occurred.'));
            setIsProcessing(false);
          }
        };
        pollJob();
      } else if (data.report) {
        setReportResult(data.report);
        setIsProcessing(false);
      } else {
        throw new Error(isPl ? 'Nieoczekiwana odpowiedź serwera.' : isUk ? 'Неочікувана відповідь сервера.' : 'Unexpected server response.');
      }
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || (isPl ? 'Wystąpił błąd podczas analizy.' : isUk ? 'Сталася помилка під час аналізу.' : 'An error occurred.'));
      setIsProcessing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!reportResult) return;
    try {
      const name = (agentConfig.nameUk || "Agri_Land_Report").replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_");
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: reportResult, fileName: name })
      });
      if (!response.ok) throw new Error(isPl ? 'Nie udało się pobrać Word pliku.' : isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const applyPresetScenario = (scenario: 'ua' | 'pl' | 'eu') => {
    if (scenario === 'ua') {
      setField1("142 ділянки (паї) / 620 га ріллі / Полтавська обл. (SHP контури полів)");
      setField2("5 виявлених накладок меж у ДЗК (перетин 0.15-0.8 га з суміжними агрофірмами)");
      setField3("38 договорів (165 га) спливають у 2026-2027 рр. — ризик втрати цілісного масиву");
      setField4("Базова НГО: 31 500 грн/га (коеф. 1.051) / Орендна плата 12% = 2 343 600 грн/рік");
      setField5("Право оренди зареєстровано в ДРРП (134 ділянки) / 8 ділянок на продовженні");
      setField6("Координати в системі СК-63 / WGS-84, наявність растрових карт обробітку");
      setNotesText("Здійснити комплексний просторовий та юридичний аудит земельного банку агропідприємства площею 620 га. Виявити та локалізувати накладки кадастрових меж ділянок за даними ДЗК/EGiB. Сформувати реєстр критичних договорів оренди, термін дії яких спливає у 2026-2027 роках, розрахувати втрати та підготувати проєкти додаткових угод на пролонгацію оренди у Word (.docx) та Excel (.xlsx) за ДСТУ 4163:2020.");
    } else if (scenario === 'pl') {
      setField1("Gospodarstwo rolne 420 ha gruntów ornych / Województwo Wielkopolskie (EGiB / Geoportal)");
      setField2("Brak nakładek granicznych w Geoportalu / 12 działek dzierżawionych od KOWR");
      setField3("Umowy dzierżawy KOWR ważne do 2031 r. / Dzierżawy prywatne 85 ha do odnowienia w 2026 r.");
      setField4("Czynsz dzierżawny KOWR przeliczany na decytony pszenicy (GUS) + podatek rolny");
      setField5("Zgłoszenie do płatności w aplikacji eWniosekPlus ARiMR (JPO, Ekoschematy, Dobrostan)");
      setField6("Wektorowe warstwy SHP zaimportowane z e-WnioskuPlus, zgodność z działkami referencyjnymi LPIS");
      setNotesText("Przeprowadzić audyt prawny i przestrzenny banku ziemi 420 ha w Polsce. Zweryfikować poprawność granic w rejestrze EGiB i dopasowanie do działek referencyjnych LPIS w ARiMR. Przygotować kalkulację dopłat bezpośrednich i ekoschematów w systemie eWniosekPlus oraz sporządzić umowy przedłużenia dzierżawy prywatnej zgodnie z Kodeksem cywilnym.");
    } else if (scenario === 'eu') {
      setField1("EU CAP Agro-Holding Portfolio / 1,200 ha Arable Land across Border Regions");
      setField2("GIS Cadastral Polygon Integration (QGIS / GeoJSON / INSPIRE Directive)");
      setField3("Cross-Compliance Audit: GAEC 1-8 Standards + SMR Statutory Management Requirements");
      setField4("EU Direct Payments + Carbon Farming Ecoschemes Portfolio: €265,000 / year");
      setField5("Land Tenure Security Index: 96% Long-term Registered Leases (5+ years horizon)");
      setField6("Deforestation-free Supply Chain Compliance (EUDR Regulation 2023/1115)");
      setNotesText("Perform comprehensive spatial GIS and legal compliance audit for 1,200 ha EU agribusiness holding. Verify compliance with EU CAP 2023-2027 conditionalities (GAEC 6 soil cover, GAEC 7 crop rotation, GAEC 8 non-productive landscape features). Model multi-year direct subsidy cash flows and export verifiable Land Bank Audit Statement in Word (.docx).");
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-16 font-mono max-w-5xl mx-auto px-4">
      {/* Top Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            🏠 {isPl ? 'Strona Główna' : isUk ? 'На головну' : 'Home'}
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            <ArrowLeft className="w-4 h-4 text-emerald-400" />
            <span>{isPl ? '← Powrót do Rolnictwa' : isUk ? '← До Центру Агробізнесу' : '← Back to Agri Hub'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md flex items-center gap-1">
            <Globe className="w-3.5 h-3.5 text-emerald-400" />
            <span>ДЗК • EGiB • CAP ЄС • ARiMR • ДАР</span>
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30 rounded-md">
            GIS SHP • GAEC • НГО 2026
          </span>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">{isPl ? "Aktywny kontekst firmy: " : isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isPl ? "Baza wiedzy przedsiębiorstwa" : isUk ? "База знань компанії" : "Company Knowledge Base")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            {isPl ? "Corporate DNA" : isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Hero Banner */}
      <div className="bg-slate-950 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl space-y-4">
        <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
          <div className="p-4 bg-emerald-950/80 border border-emerald-500/40 rounded-2xl text-emerald-400 shrink-0 shadow-lg">
            <AgentIcon name={agentConfig.icon} className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-200 border border-emerald-400/40 rounded">
                {getLocalizedAgentCategory(agentConfig, language)}
              </span>
              <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-sky-500/30 text-sky-200 border border-sky-400/40 rounded flex items-center gap-1">
                <ShieldCheck className="w-3 h-3 text-sky-400" />
                <span>100% Агро-Кадастровий & Земельний Комплаєнс</span>
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-extrabold uppercase tracking-wide text-white">
              {getLocalizedAgentName(agentConfig, language)}
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
              {getLocalizedAgentDesc(agentConfig, language)}
            </p>
          </div>
        </div>
      </div>

      {/* 1-Click Fast Presets */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-white space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-extrabold uppercase tracking-wide flex items-center gap-1.5 text-amber-400">
            <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
            <span>{isPl ? "⚡ Gotowe scenariusze agrotechniczne & GIS (1 kliknięcie):" : isUk ? "⚡ Готові сценарії агробізнесу та ГІС (1 клік):" : "⚡ Ready Agri & GIS Scenarios (1-Click Fill):"}</span>
          </span>
          <span className="text-[10px] text-slate-400 font-mono">ДЗК • EGiB • CAP ЄС • ARiMR • SHP</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => applyPresetScenario('ua')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-emerald-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-emerald-300 group-hover:text-emerald-200 flex items-center gap-1.5">
              <span>🇺🇦 Земельний аудит 620 га (Україна)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">ДЗК накладки • НГО 2026 • Оренда</div>
          </button>

          <button
            type="button"
            onClick={() => applyPresetScenario('pl')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-blue-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-blue-300 group-hover:text-blue-200 flex items-center gap-1.5">
              <span>🇵🇱 Bank Ziemi & ARiMR 420 ha (Polska)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">EGiB • eWniosekPlus • KOWR • GAEC</div>
          </button>

          <button
            type="button"
            onClick={() => applyPresetScenario('eu')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-purple-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-purple-300 group-hover:text-purple-200 flex items-center gap-1.5">
              <span>🇪🇺 EU CAP Portfolio 1200 ha (EU)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">INSPIRE • Carbon Farming • EUDR</div>
          </button>
        </div>
      </div>

      {/* Main Parameters Form */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-6 text-white shadow-xl">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-emerald-300 flex items-center gap-2 border-b border-slate-800 pb-3">
          <Tractor className="w-4 h-4 text-emerald-400" />
          <span>{isPl ? 'PARAMETRY BANKU ZIEMI, GIS I DOPŁAT:' : isUk ? 'ВХІДНІ ПАРАМЕТРИ ЗЕМЕЛЬНОГО БАНКУ, ГІС ТА СУБСИДІЙ:' : 'LAND BANK, GIS & SUBSIDY PARAMETERS:'}</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">1. {getFieldLabel(defaultDemo, 1, language)}</label>
            <input
              type="text"
              value={field1}
              onChange={e => setField1(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">2. {getFieldLabel(defaultDemo, 2, language)}</label>
            <input
              type="text"
              value={field2}
              onChange={e => setField2(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">3. {getFieldLabel(defaultDemo, 3, language)}</label>
            <input
              type="text"
              value={field3}
              onChange={e => setField3(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">4. {getFieldLabel(defaultDemo, 4, language)}</label>
            <input
              type="text"
              value={field4}
              onChange={e => setField4(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">5. {getFieldLabel(defaultDemo, 5, language)}</label>
            <input
              type="text"
              value={field5}
              onChange={e => setField5(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">6. {getFieldLabel(defaultDemo, 6, language)}</label>
            <input
              type="text"
              value={field6}
              onChange={e => setField6(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">
            {isPl ? '7. ZAKRES AUDYTU I WYMAGANIA FORMALNO-PRAWNE:' : isUk ? '7. ДЕТАЛЬНИЙ ОПИС ЗЕМЕЛЬНОГО БАНКУ, ПОЛІВ ТА ЗАВДАННЯ АУДИТУ:' : '7. AUDIT INSTRUCTIONS & LAND TENURE SPECIFICATIONS:'}
          </label>
          <textarea 
            rows={5} 
            value={notesText} 
            onChange={e => setNotesText(e.target.value)} 
            className="w-full p-3.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono leading-relaxed" 
          />
        </div>

        {/* File Attachments */}
        <div className="space-y-3 pt-4 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center gap-2">
              <Upload className="w-4 h-4 text-emerald-400" />
              <span>{isPl ? 'ZAŁĄCZNIKI (PLIKI SHP / KML / GEOJSON / WYCIĄGI EGIB / PDF / XLSX):' : isUk ? 'СУПРОВІДНІ ФАЙЛИ ГІС (SHP / KML / GEOJSON), ВИТЯГИ ДЗК ТА РЕЄСТРИ (PDF / XLSX):' : 'SUPPORTING GIS SHP, KML, CADASTRE EXTRACTS & SPREADSHEETS:'}</span>
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="px-4 py-2.5 bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-500/40 text-emerald-200 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2">
              <Upload className="w-4 h-4 text-emerald-400" />
              <span>{isReadingFile ? (isPl ? 'Odczytywanie...' : 'Зчитування...') : (isPl ? '➕ Dodaj pliki GIS / ДЗК' : '➕ Завантажити дані')}</span>
              <input type="file" multiple accept=".shp,.kml,.geojson,.json,.pdf,.docx,.xlsx,.csv,.txt" onChange={handleFileUpload} className="hidden" />
            </label>

            {attachedFiles.map((file, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-slate-200">
                <FileCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span className="font-mono text-[11px] max-w-[150px] truncate">{file.name}</span>
                <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-400 p-0.5">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {errorMessage && (
          <div className="p-3.5 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Action Button */}
        <div className="pt-4 border-t border-slate-800 flex justify-end">
          <button
            onClick={handleRunAnalysis}
            disabled={isProcessing || !notesText.trim()}
            className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 hover:scale-[1.01]"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-white" />
                <span>{isPl ? LOADING_STEPS_PL[loadingStepIndex] : isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span>{isPl ? '🚀 ROZPOCZNIJ AUDYT ZIEMSKO-ROLNICZY' : isUk ? '🚀 ЗАПУСТИТИ ЗЕМЕЛЬНИЙ & АГРО-АУДИТ' : '🚀 RUN AGRI & LAND AUDIT'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Result Section */}
      {reportResult && (
        <div className="bg-slate-950 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 space-y-4 text-white shadow-2xl animate-fade-in">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-2">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-emerald-300 flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-400" />
              <span>{isPl ? 'OFICJALNY RAPORT AUDYTU ZIEMSKO-PRAWNEGO I DOPŁAT:' : isUk ? 'ОФІЦІЙНИЙ ВИСНОВОК АУДИТУ ЗЕМЕЛЬНОГО БАНКУ & СУБСИДІЙ:' : 'OFFICIAL AGRI LAND AUDIT & SUBSIDY DOSSIER:'}</span>
            </h3>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleDownloadWord}
                className="px-3 py-1.5 bg-emerald-950 hover:bg-emerald-900 border border-emerald-500/40 text-emerald-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <Download className="w-3.5 h-3.5 text-emerald-400" />
                <span>Word (.docx)</span>
              </button>

              <ExcelExportButton
                title={agentConfig.nameUk || 'Agri Land'}
                content={reportResult}
                fileName={`${agentConfig.nameUk || 'Agri_Land'}_data`}
                className="px-3 py-1.5 bg-teal-950 hover:bg-teal-900 border border-teal-500/40 text-teal-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
                isUk={isUk}
              />

              <TelegramExportButton
                title={agentConfig.nameUk || 'Agri Land'}
                content={reportResult}
                agentName={getLocalizedAgentName(agentConfig, language)}
                agentId={agentId}
                isUk={isUk}
              />

              <button
                onClick={() => setShowSignModal(true)}
                className="px-3 py-1.5 bg-amber-950 hover:bg-amber-900 border border-amber-500/40 text-amber-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                <span>{isPl ? '✍️ Podpisz QES' : isUk ? '✍️ Підписати КЕП' : '✍️ Sign QES'}</span>
              </button>

              <button
                onClick={() => {
                  navigator.clipboard.writeText(reportResult);
                  setIsCopied(true);
                  setTimeout(() => setIsCopied(false), 2000);
                }}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-slate-300" />}
                <span>{isCopied ? (isPl ? 'Skopiowano!' : isUk ? 'Скопійовано!' : 'Copied!') : (isPl ? 'Kopiuj' : isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
            </div>
          </div>

          <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-5 text-xs font-mono leading-relaxed text-slate-200 whitespace-pre-wrap max-h-[650px] overflow-y-auto">
            {reportResult}
          </div>
        </div>
      )}

      {showSignModal && reportResult && (
        <DigitalSignatureModal
          isOpen={showSignModal}
          onClose={() => setShowSignModal(false)}
          documentText={reportResult}
          documentTitle={getLocalizedAgentName(agentConfig, language)}
          language={language}
        />
      )}
    </div>
  );
}
